import { useTranslation } from 'react-i18next';
import { Combobox } from '@/components/ui/combobox';
import { trpc } from '@/lib/trpc';

export type CounterpartyKind = 'customer' | 'vendor';

export interface CounterpartySelection {
  kind: CounterpartyKind;
  id: string;
  companyName: string | null;
  contactName: string | null;
  email: string | null;
  phone: string | null;
  // Useful for downstream pricing logic (Phase 2): tells us if this is a
  // M-customer (retail price) or B/S-vendor (vendor price).
  cariType: 'M' | 'T' | 'S' | 'B';
}

interface Props {
  customerId: string | null | undefined;
  supplierId: string | null | undefined;
  onSelect: (selection: CounterpartySelection) => void;
  onClear: () => void;
  placeholder?: string;
  // When false, hides B/S vendors from the dropdown (legacy customer-only forms).
  allowVendors?: boolean;
  disabled?: boolean;
  selectedLabel?: string;
}

export function CounterpartyPicker({
  customerId,
  supplierId,
  onSelect,
  onClear,
  placeholder,
  allowVendors = true,
  disabled = false,
  selectedLabel,
}: Props) {
  const { t } = useTranslation('trading');
  const { data } = trpc.tradingCari.list.useQuery({ pageSize: 500 });
  const items = data?.items ?? [];

  // Sales-side documents target commercial counterparties only (M/T/S/B).
  // GL-only types (ORT/P/AH/E/X/I) don't belong on sales quotes/orders/invoices.
  const COMMERCIAL_TYPES = ['M', 'T', 'S', 'B'];
  const commercialItems = items.filter((c) => COMMERCIAL_TYPES.includes(c.cariType));
  const filtered = allowVendors
    ? commercialItems
    : commercialItems.filter((c) => c.cariType === 'M');

  const value = supplierId
    ? `vendor:${supplierId}`
    : customerId
      ? `customer:${customerId}`
      : '';

  const typeLabels: Record<'M' | 'T' | 'S' | 'B', string> = {
    M: t('cari.customer'),
    T: t('cari.supplier'),
    S: t('cari.vendor'),
    B: t('cari.dealer'),
  };

  // Composite value `kind:id` lets one Combobox handle both sides without
  // colliding when a customer and a supplier happen to share an id (cuid
  // makes that astronomically unlikely, but the prefix also doubles as a
  // human-readable type label in the dropdown).
  const options = filtered.map((c) => ({
    value: `${c.cariType === 'M' ? 'customer' : 'vendor'}:${c.id}`,
    label: `${c.companyName || c.contactName || ''} · ${typeLabels[c.cariType as 'M' | 'T' | 'S' | 'B']}`,
  }));
  if (value && selectedLabel && !options.some((option) => option.value === value)) {
    options.unshift({ value, label: selectedLabel });
  }

  return (
    <Combobox
      disabled={disabled}
      options={options}
      value={value}
      onValueChange={(v) => {
        if (!v) {
          onClear();
          return;
        }
        const [kindRaw, id] = v.split(':');
        const cari = filtered.find((c) => c.id === id);
        if (!cari) return;
        onSelect({
          kind: kindRaw === 'vendor' ? 'vendor' : 'customer',
          id: cari.id,
          companyName: cari.companyName ?? null,
          contactName: cari.contactName ?? null,
          email: cari.email ?? null,
          phone: cari.phone ?? null,
          cariType: cari.cariType as 'M' | 'T' | 'S' | 'B',
        });
      }}
      placeholder={placeholder ?? t('sales.orders.selectCustomer')}
      searchPlaceholder={t('cari.searchPlaceholder')}
    />
  );
}
